<template>
  <form @submit.prevent="cancelOrder">
    <div class="row">
      <div class="col-12 mb-3">
        <label class="form-label">Order Status</label>
        <input
          type="text"
          class="form-control"
          :value="order?.order_status || 'N/A'"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Paid Amount</label>
        <input
          type="text"
          class="form-control"
          :value="formatCurrency(order?.paid_amount || 0)"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Refund Amount</label>
        <input
          v-model.number="form.refund_amount"
          type="number"
          class="form-control"
          :class="{ 'is-invalid': errors.refund_amount }"
          step="0.01"
          min="0"
          :max="order?.paid_amount || 0"
        >
        <div v-if="errors.refund_amount" class="invalid-feedback">
          {{ errors.refund_amount[0] }}
        </div>
        <small class="form-text text-muted">
          Maximum refund amount: {{ formatCurrency(order?.paid_amount || 0) }}
        </small>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Cancellation Reason</label>
        <textarea
          v-model="form.reason"
          class="form-control"
          :class="{ 'is-invalid': errors.reason }"
          rows="4"
          placeholder="Please provide a detailed reason for cancellation (minimum 10 characters)"
          minlength="10"
          required
        ></textarea>
        <div v-if="errors.reason" class="invalid-feedback">
          {{ errors.reason[0] }}
        </div>
        <small class="form-text text-muted">
          Minimum 10 characters required
        </small>
      </div>

      <div class="col-12">
        <div class="alert alert-warning" role="alert">
          <strong>Warning:</strong> This action cannot be undone. The order will be permanently cancelled.
        </div>
      </div>

      <div class="col-12">
        <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
          Close
        </button>
        <button type="submit" class="btn btn-danger" :disabled="loading || isOrderAlreadyCancelled">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Cancel Order
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'OrderCancel',

  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      form: {
        reason: '',
        refund_amount: 0
      },
      errors: {},
      loading: false
    };
  },

  computed: {
    isOrderAlreadyCancelled() {
      return this.order?.order_status === 'cancelled';
    }
  },

  watch: {
    order: {
      handler(newOrder) {
        if (newOrder && newOrder.paid_amount) {
          // Set default refund amount to paid amount
          this.form.refund_amount = newOrder.paid_amount;
        }
      },
      immediate: true
    }
  },

  mounted() {
    console.log('OrderCancel component mounted');
    console.log('Order data:', this.order);

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  },

  methods: {
    async cancelOrder() {
      this.loading = true;
      this.errors = {};

      try {
        // Client-side validation
        if (this.isOrderAlreadyCancelled) {
          this.errors.status = ['Order is already cancelled'];
          this.loading = false;
          return;
        }

        if (this.form.refund_amount > (this.order?.paid_amount || 0)) {
          this.errors.refund_amount = ['Refund amount cannot exceed paid amount'];
          this.loading = false;
          return;
        }

        if (!this.form.reason || this.form.reason.length < 10) {
          this.errors.reason = ['Reason must be at least 10 characters long'];
          this.loading = false;
          return;
        }

        const response = await this.$http.post(`/api/orders/${this.order.id}/cancel`, {
          reason: this.form.reason,
          refund_amount: this.form.refund_amount
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Reset form
          this.resetForm();

          // Close modal
          const modal = this.$el.closest('.modal');
          if (modal) {
            const modalInstance = bootstrap.Modal.getInstance(modal);
            if (modalInstance) {
              modalInstance.hide();
            }
          }

          // Emit event for parent components
          this.$emit('order-cancelled', response.data.order);

          // Redirect if specified
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            // Refresh page to show updated order
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Order cancellation failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while cancelling the order'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        reason: '',
        refund_amount: 0
      };
      this.errors = {};
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    showSuccessMessage,
    showErrorMessage
  }
}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

.alert-warning {
  border-color: #ffeaa7;
  background-color: #ffeaa7;
  color: #2d3436;
}
</style>
